import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateShortCode, getCompanyCodeFromShort, decryptText } from '../lib/routeEncryption';

/**
 * Hook para navegación segura con códigos de empresa encriptados
 */
export const useSecureNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * Navega a una ruta con código de empresa encriptado
     */
    const navigateToCompany = useCallback((companyCode: string, path: string) => {
        try {
            const shortCode = generateShortCode(companyCode);
            navigate(`/${shortCode}${path}`);
        } catch (error) {
            console.error('Error al navegar a empresa:', error);
            // Fallback a navegación normal sin encriptación
            navigate(`/${companyCode}${path}`);
        }
    }, [navigate]);

    /**
     * Genera una URL segura con código de empresa encriptado
     */
    const getSecureCompanyUrl = useCallback((companyCode: string, path: string): string => {
        try {
            const shortCode = generateShortCode(companyCode);
            // Obtener el dominio actual para construir la URL completa
            const currentDomain = window.location.origin;
            return `${currentDomain}/${shortCode}${path}`;
        } catch (error) {
            console.error('Error al generar URL segura:', error);
            // Fallback a URL normal sin encriptación
            const currentDomain = window.location.origin;
            return `${currentDomain}/${companyCode}${path}`;
        }
    }, []);

    /**
     * Extrae el código de empresa de la URL actual
     */
    const getCurrentCompanyCode = useCallback((): string | null => {
        try {
                        const pathSegments = location.pathname.split('/').filter(Boolean);
            
            if (pathSegments.length === 0) {
                                return null;
            }

            const firstSegment = pathSegments[0];
            
            // Si el primer segmento es 'area', no es un código de empresa
            if (firstSegment === 'area' || firstSegment === 'portal') {
                                return null;
            }

            // Primero intentar con código corto
            try {
                                const companyCode = getCompanyCodeFromShort(firstSegment);
                if (companyCode) {
                                        return companyCode;
                }
                            } catch (shortCodeError) {
                            }

            // Fallback: intentar desencriptar el primer segmento (compatibilidad)
            // Solo intentar desencriptar si parece ser texto encriptado (longitud > 10 y caracteres especiales)
            if (firstSegment.length > 10 && /^[a-zA-Z0-9_-]+$/.test(firstSegment)) {
                                try {
                    const decrypted = decryptText(firstSegment);
                                        return decrypted;
                } catch (decryptError) {
                                    }
            }

            // Si no se puede desencriptar, asumir que es un código sin encriptar
            // pero solo si no es una ruta del sistema
            if (!['area', 'portal', 'admin', 'auth'].includes(firstSegment)) {
                                return firstSegment;
            }

                        return null;
        } catch (error) {
            console.error('❌ Error general al extraer código de empresa:', error);
            return null;
        }
    }, [location]);

    /**
     * Verifica si la ruta actual contiene un código de empresa
     */
    const hasCompanyCode = useCallback((): boolean => {
        return getCurrentCompanyCode() !== null;
    }, [getCurrentCompanyCode]);

    /**
     * Navega a la página de login con código de empresa
     */
    const navigateToCompanyLogin = useCallback((companyCode: string) => {
        navigateToCompany(companyCode, '/area/login');
    }, [navigateToCompany]);

    /**
     * Navega a la página de fichaje con código de empresa
     */
    const navigateToCompanyPunch = useCallback((companyCode: string) => {
        navigateToCompany(companyCode, '/area/fichar');
    }, [navigateToCompany]);

    /**
     * Navega al portal del empleado con código de empresa
     */
    const navigateToEmployeePortal = useCallback((companyCode: string) => {
        navigateToCompany(companyCode, '/portal/login');
    }, [navigateToCompany]);

    /**
     * Navega al dashboard del empleado con código de empresa
     */
    const navigateToEmployeeDashboard = useCallback((companyCode: string) => {
        navigateToCompany(companyCode, '/portal/dashboard');
    }, [navigateToCompany]);

    /**
     * Navega a cualquier ruta con el código de empresa actual o especificado
     * @param path Ruta relativa (ej: '/portal/dashboard', '/portal/team')
     * @param companyCode Opcional - código de empresa a usar, si no se usa el actual
     */
    const secureNavigate = useCallback((path: string, companyCode?: string) => {
        const code = companyCode || getCurrentCompanyCode();
        if (!code) {
            console.error('❌ No hay código de empresa disponible para navegación segura');
            navigate(path);
            return;
        }
        navigateToCompany(code, path);
    }, [navigateToCompany, getCurrentCompanyCode, navigate]);

    /**
     * Genera URLs para diferentes áreas de la aplicación
     */
    const getCompanyUrls = useCallback((companyCode: string) => ({
        login: getSecureCompanyUrl(companyCode, '/area/login'),
        punch: getSecureCompanyUrl(companyCode, '/area/fichar'),
        employeeLogin: getSecureCompanyUrl(companyCode, '/portal/login'),
        employeeDashboard: getSecureCompanyUrl(companyCode, '/portal/dashboard'),
        employeeSchedule: getSecureCompanyUrl(companyCode, '/portal/horario'),
        employeeRecords: getSecureCompanyUrl(companyCode, '/portal/registros'),
        employeeProfile: getSecureCompanyUrl(companyCode, '/portal/perfil'),
        employeeAbsence: getSecureCompanyUrl(companyCode, '/portal/solicitar-ausencia'),
    }), [getSecureCompanyUrl]);

    return {
        navigateToCompany,
        getSecureCompanyUrl,
        getCurrentCompanyCode,
        hasCompanyCode,
        navigateToCompanyLogin,
        navigateToCompanyPunch,
        navigateToEmployeePortal,
        navigateToEmployeeDashboard,
        secureNavigate,
        getCompanyUrls,
    };
};

export default useSecureNavigation;